import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API
    from "../../utils/apiCalling";

import {
    config,
} from "../../utils/apiUrl";


const api =
    new API({
        tokenKey:
            "platformAccessToken",
    });


/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {

    /*
    |--------------------------------------------------------------------------
    | LIST
    |--------------------------------------------------------------------------
    */

    users: [],

    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 1,
    },

    filters: {
        page: 1,
        limit: 20,
        search: "",
        status: "",
        userType: "",
        tenantId: "",
        roleId: "",
    },

    listLoading: false,
    listError: null,


    /*
    |--------------------------------------------------------------------------
    | FILTER OPTIONS
    |--------------------------------------------------------------------------
    */

    filterOptions: {
        tenants: [],
        roles: [],
    },

    filtersLoading: false,
    filtersError: null,


    /*
    |--------------------------------------------------------------------------
    | DETAILS
    |--------------------------------------------------------------------------
    */

    selectedUser: null,

    detailsLoading: false,
    detailsError: null,


    /*
    |--------------------------------------------------------------------------
    | STATUS UPDATE
    |--------------------------------------------------------------------------
    */

    statusLoading: false,

    statusUserId: null,

    statusError: null,
};


/*
|--------------------------------------------------------------------------
| BUILD QUERY STRING
|--------------------------------------------------------------------------
*/

const buildQueryString = (
    params = {}
) => {

    const searchParams =
        new URLSearchParams();


    Object.entries(
        params
    ).forEach(
        ([
            key,
            value,
        ]) => {

            if (
                value ===
                    undefined ||
                value ===
                    null ||
                value ===
                    ""
            ) {

                return;

            }


            searchParams.append(
                key,
                value
            );

        }
    );


    return searchParams
        .toString();

};


/*
|--------------------------------------------------------------------------
| NORMALIZE ERROR
|--------------------------------------------------------------------------
*/

const getErrorPayload = (
    error
) => {

    return {
        message:
            error?.response?.data?.message ||
            error?.response?.message ||
            error?.message ||
            "Something went wrong.",
    };

};


/*
|--------------------------------------------------------------------------
| FETCH TENANT USERS
|--------------------------------------------------------------------------
*/

export const fetchPlatformTenantUsers =
    createAsyncThunk(
        "platformTenantUser/fetchPlatformTenantUsers",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {

            try {

                const query =
                    buildQueryString(
                        params
                    );


                const url =
                    query
                        ? `${config.platformTenantUsers}?${query}`
                        : config.platformTenantUsers;


                const response =
                    await api.get(
                        url,
                        true
                    );


                return response;

            } catch (
                error
            ) {

                return rejectWithValue(
                    getErrorPayload(
                        error
                    )
                );

            }

        }
    );


/*
|--------------------------------------------------------------------------
| FETCH FILTER OPTIONS
|--------------------------------------------------------------------------
*/

export const fetchPlatformTenantUserFilters =
    createAsyncThunk(
        "platformTenantUser/fetchPlatformTenantUserFilters",

        async (
            _,
            {
                rejectWithValue,
            }
        ) => {

            try {

                const response =
                    await api.get(
                        config
                            .platformTenantUserFilters,
                        true
                    );


                return response;

            } catch (
                error
            ) {

                return rejectWithValue(
                    getErrorPayload(
                        error
                    )
                );

            }

        }
    );


/*
|--------------------------------------------------------------------------
| FETCH USER BY ID
|--------------------------------------------------------------------------
*/

export const fetchPlatformTenantUserById =
    createAsyncThunk(
        "platformTenantUser/fetchPlatformTenantUserById",

        async (
            id,
            {
                rejectWithValue,
            }
        ) => {

            try {

                const response =
                    await api.get(
                        config
                            .platformTenantUserById(
                                id
                            ),
                        true
                    );


                return response;

            } catch (
                error
            ) {

                return rejectWithValue(
                    getErrorPayload(
                        error
                    )
                );

            }

        }
    );


/*
|--------------------------------------------------------------------------
| UPDATE USER STATUS
|--------------------------------------------------------------------------
*/

export const updatePlatformTenantUserStatus =
    createAsyncThunk(
        "platformTenantUser/updatePlatformTenantUserStatus",

        async (
            {
                id,
                status,
            },
            {
                rejectWithValue,
            }
        ) => {

            try {

                const response =
                    await api.patch(
                        config
                            .platformTenantUserStatus(
                                id
                            ),

                        {
                            status,
                        },

                        true
                    );


                return {
                    response,

                    userId:
                        id,

                    requestedStatus:
                        status,
                };

            } catch (
                error
            ) {

                return rejectWithValue(
                    getErrorPayload(
                        error
                    )
                );

            }

        }
    );


/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const platformTenantUserSlice =
    createSlice({

        name:
            "platformTenantUser",

        initialState,

        reducers: {

            /*
            |--------------------------------------------------------------------------
            | SET FILTER
            |--------------------------------------------------------------------------
            */

            setPlatformTenantUserFilter:
                (
                    state,
                    action
                ) => {

                    const {
                        key,
                        value,
                    } =
                        action.payload ||
                        {};


                    if (
                        !key
                    ) {

                        return;

                    }


                    state.filters[
                        key
                    ] =
                        value;

                },


            /*
            |--------------------------------------------------------------------------
            | SET FILTERS
            |--------------------------------------------------------------------------
            */

            setPlatformTenantUserFilters:
                (
                    state,
                    action
                ) => {

                    state.filters = {
                        ...state.filters,
                        ...(action.payload || {}),
                    };

                },


            /*
            |--------------------------------------------------------------------------
            | RESET FILTERS
            |--------------------------------------------------------------------------
            */

            resetPlatformTenantUserFilters:
                (
                    state
                ) => {

                    state.filters = {
                        page: 1,
                        limit: 20,
                        search: "",
                        status: "",
                        userType: "",
                        tenantId: "",
                        roleId: "",
                    };

                },


            /*
            |--------------------------------------------------------------------------
            | CLEAR SELECTED USER
            |--------------------------------------------------------------------------
            */

            clearSelectedPlatformTenantUser:
                (
                    state
                ) => {

                    state.selectedUser =
                        null;

                    state.detailsError =
                        null;

                },


            /*
            |--------------------------------------------------------------------------
            | CLEAR ERRORS
            |--------------------------------------------------------------------------
            */

            clearPlatformTenantUserErrors:
                (
                    state
                ) => {

                    state.listError =
                        null;

                    state.filtersError =
                        null;

                    state.detailsError =
                        null;

                    state.statusError =
                        null;

                },

        },


        /*
        |--------------------------------------------------------------------------
        | EXTRA REDUCERS
        |--------------------------------------------------------------------------
        */

        extraReducers:
            (
                builder
            ) => {

                /*
                |--------------------------------------------------------------------------
                | FETCH USERS
                |--------------------------------------------------------------------------
                */

                builder
                    .addCase(
                        fetchPlatformTenantUsers
                            .pending,

                        (
                            state
                        ) => {

                            state.listLoading =
                                true;

                            state.listError =
                                null;

                        }
                    )
                    .addCase(
                        fetchPlatformTenantUsers
                            .fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state.listLoading =
                                false;


                            const payload =
                                action.payload ||
                                {};


                            state.users =
                                Array.isArray(
                                    payload?.data
                                )
                                    ? payload.data
                                    : [];


                            state.pagination = {
                                page:
                                    payload
                                        ?.pagination
                                        ?.page ||
                                    state.filters
                                        .page ||
                                    1,

                                limit:
                                    payload
                                        ?.pagination
                                        ?.limit ||
                                    state.filters
                                        .limit ||
                                    20,

                                total:
                                    payload
                                        ?.pagination
                                        ?.total ||
                                    0,

                                totalPages:
                                    payload
                                        ?.pagination
                                        ?.totalPages ||
                                    1,
                            };

                        }
                    )
                    .addCase(
                        fetchPlatformTenantUsers
                            .rejected,

                        (
                            state,
                            action
                        ) => {

                            state.listLoading =
                                false;

                            state.listError =
                                action.payload ||
                                {
                                    message:
                                        "Unable to load tenant users.",
                                };

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | FETCH FILTERS
                |--------------------------------------------------------------------------
                */

                builder
                    .addCase(
                        fetchPlatformTenantUserFilters
                            .pending,

                        (
                            state
                        ) => {

                            state.filtersLoading =
                                true;

                            state.filtersError =
                                null;

                        }
                    )
                    .addCase(
                        fetchPlatformTenantUserFilters
                            .fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state.filtersLoading =
                                false;


                            const payload =
                                action.payload ||
                                {};


                            state.filterOptions = {
                                tenants:
                                    Array.isArray(
                                        payload
                                            ?.data
                                            ?.tenants
                                    )
                                        ? payload
                                            .data
                                            .tenants
                                        : [],

                                roles:
                                    Array.isArray(
                                        payload
                                            ?.data
                                            ?.roles
                                    )
                                        ? payload
                                            .data
                                            .roles
                                        : [],
                            };

                        }
                    )
                    .addCase(
                        fetchPlatformTenantUserFilters
                            .rejected,

                        (
                            state,
                            action
                        ) => {

                            state.filtersLoading =
                                false;

                            state.filtersError =
                                action.payload ||
                                {
                                    message:
                                        "Unable to load filters.",
                                };

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | FETCH USER DETAILS
                |--------------------------------------------------------------------------
                */

                builder
                    .addCase(
                        fetchPlatformTenantUserById
                            .pending,

                        (
                            state
                        ) => {

                            state.detailsLoading =
                                true;

                            state.detailsError =
                                null;

                        }
                    )
                    .addCase(
                        fetchPlatformTenantUserById
                            .fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state.detailsLoading =
                                false;

                            state.selectedUser =
                                action
                                    .payload
                                    ?.data ||
                                null;

                        }
                    )
                    .addCase(
                        fetchPlatformTenantUserById
                            .rejected,

                        (
                            state,
                            action
                        ) => {

                            state.detailsLoading =
                                false;

                            state.detailsError =
                                action.payload ||
                                {
                                    message:
                                        "Unable to load tenant user.",
                                };

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | UPDATE STATUS
                |--------------------------------------------------------------------------
                */

                builder
                    .addCase(
                        updatePlatformTenantUserStatus
                            .pending,

                        (
                            state,
                            action
                        ) => {

                            state.statusLoading =
                                true;

                            state.statusError =
                                null;

                            state.statusUserId =
                                action
                                    .meta
                                    ?.arg
                                    ?.id ||
                                null;

                        }
                    )
                    .addCase(
                        updatePlatformTenantUserStatus
                            .fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state.statusLoading =
                                false;

                            state.statusUserId =
                                null;


                            const updatedUser =
                                action
                                    .payload
                                    ?.response
                                    ?.data ||
                                null;


                            if (
                                !updatedUser
                            ) {

                                return;

                            }


                            const updatedUserId =
                                String(
                                    updatedUser
                                        ?._id ||
                                    updatedUser
                                        ?.id ||
                                    action
                                        .payload
                                        ?.userId ||
                                    ""
                                );


                            /*
                            |--------------------------------------------------------------------------
                            | UPDATE LIST RECORD
                            |--------------------------------------------------------------------------
                            */

                            state.users =
                                state.users.map(
                                    (
                                        user
                                    ) => {

                                        const userId =
                                            String(
                                                user
                                                    ?._id ||
                                                user
                                                    ?.id ||
                                                ""
                                            );


                                        return (
                                            userId ===
                                            updatedUserId
                                        )
                                            ? updatedUser
                                            : user;

                                    }
                                );


                            /*
                            |--------------------------------------------------------------------------
                            | UPDATE SELECTED USER
                            |--------------------------------------------------------------------------
                            */

                            const selectedUserId =
                                String(
                                    state
                                        .selectedUser
                                        ?._id ||
                                    state
                                        .selectedUser
                                        ?.id ||
                                    ""
                                );


                            if (
                                selectedUserId &&
                                selectedUserId ===
                                updatedUserId
                            ) {

                                state.selectedUser =
                                    updatedUser;

                            }

                        }
                    )
                    .addCase(
                        updatePlatformTenantUserStatus
                            .rejected,

                        (
                            state,
                            action
                        ) => {

                            state.statusLoading =
                                false;

                            state.statusUserId =
                                null;

                            state.statusError =
                                action.payload ||
                                {
                                    message:
                                        "Unable to update tenant user status.",
                                };

                        }
                    );

            },

    });


/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    setPlatformTenantUserFilter,
    setPlatformTenantUserFilters,
    resetPlatformTenantUserFilters,
    clearSelectedPlatformTenantUser,
    clearPlatformTenantUserErrors,
} =
    platformTenantUserSlice.actions;


/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
*/

export const selectPlatformTenantUsers =
    (
        state
    ) =>
        state
            .platformTenantUser
            ?.users ||
        [];


export const selectPlatformTenantUserPagination =
    (
        state
    ) =>
        state
            .platformTenantUser
            ?.pagination ||
        initialState.pagination;


export const selectPlatformTenantUserFilters =
    (
        state
    ) =>
        state
            .platformTenantUser
            ?.filters ||
        initialState.filters;


export const selectPlatformTenantUserFilterOptions =
    (
        state
    ) =>
        state
            .platformTenantUser
            ?.filterOptions ||
        initialState.filterOptions;


export const selectPlatformTenantUserListLoading =
    (
        state
    ) =>
        Boolean(
            state
                .platformTenantUser
                ?.listLoading
        );


export const selectPlatformTenantUserFiltersLoading =
    (
        state
    ) =>
        Boolean(
            state
                .platformTenantUser
                ?.filtersLoading
        );


export const selectSelectedPlatformTenantUser =
    (
        state
    ) =>
        state
            .platformTenantUser
            ?.selectedUser ||
        null;


export const selectPlatformTenantUserDetailsLoading =
    (
        state
    ) =>
        Boolean(
            state
                .platformTenantUser
                ?.detailsLoading
        );


export const selectPlatformTenantUserStatusLoading =
    (
        state
    ) =>
        Boolean(
            state
                .platformTenantUser
                ?.statusLoading
        );


export const selectPlatformTenantUserStatusUserId =
    (
        state
    ) =>
        state
            .platformTenantUser
            ?.statusUserId ||
        null;


export const selectPlatformTenantUserListError =
    (
        state
    ) =>
        state
            .platformTenantUser
            ?.listError ||
        null;


export const selectPlatformTenantUserDetailsError =
    (
        state
    ) =>
        state
            .platformTenantUser
            ?.detailsError ||
        null;


/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default platformTenantUserSlice.reducer;